/* eslint-disable react/no-array-index-key */
import React, {
  useRef, useCallback, useContext, useEffect, useState, useMemo
} from 'react';
import { string, number } from 'prop-types';
import { BrowseSearchRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import classNames from 'classnames';
import {
  client,
  extend,
  QueryProvider,
  QueryContext,
  hoist,
  useLazyDataModel,
  params,
  shape as shapeType,
  string as stringType,
  alias
} from '@thd-nucleus/data-sources';
import { useHistory, useLocation } from '@thd-olt-component-react/router';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import {
  ProductResults,
  ResultsApplied,
  ResultsFilterAndSort,
  ResultsHeader,
  ResultsPagination,
  ResultsWrapped,
  ResultsSortBy,
  ResultsDimensions,
  useProductResults,
  productDefaultResultsDataModel,
  updateQueryParams,
  parseUrl,
  ProductResultsContext
} from '@thd-olt-component-react/product-results';
import {
  ProductPodDefault,
  ProductPodPlaceholder,
  ProductPodUtils
} from '@thd-olt-component-react/product-pod';
import {
  ProductPodGroup,
  CustomProductPodDetails
} from '@thd-olt-component-react/product-pod-group';
import { RectShape, TextBlock } from '@thd-olt-component-react/loading-placeholder';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CategoryCards } from '@thd-olt-component-react/category-cards';
import { LayoutManager, LayoutGroup, LayoutGroupContext } from '@thd-olt-component-react/layout-manager';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { EtchWidgets } from '@thd-olt-component-react/etch-widgets';
import { VisualCategoryNavigation } from '@thd-olt-component-react/visual-category-navigation';
import { DynamicBroadIntentAisle } from '@thd-olt-component-react/dynamic-broad-intent-aisle';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import {
  RecommendationsProvider,
  RecommendationsPlaceholder
} from '@thd-olt-component-react/recommendations';
import { SponsoredBanner, SponsoredSkyscraperBanner } from '@thd-olt-component-react/sponsored-content';
import { Metadata, BrowseSearchMetadata } from '@thd-olt-component-react/metadata';
import { CompareTray } from '@thd-olt-component-react/compare';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { NoResultsFound } from '@thd-olt-component-react/no-results-found';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import {
  SeoGeneratedContentLoad, SeoGeneratedContent, SeoGeneratedContentModel,
  SeoLinksLoad, SeoLinks, SeoLinksModel, productPageImport
} from './dynamic-imports';
import { HYDRATION_STANDARD_SCROLL_BUFFER,
  SERVER_SIDE_PRODUCT_POD_COUNT,
  CLIENT_SIDE_PRODUCT_POD_COUNT,
  MAX_PODS_TO_HYDRATE } from '../../constants';
import '../../styles/browse-search.scss';

const HoistedProductPodGroup = hoist(ProductPodGroup, ProductPodDefault, CategoryCards, CustomProductPodDetails);

export const BrowseAndSearch = (props) => {
  declareContexts([ProductResultsContext, QueryContext, ExperienceContext, LayoutGroupContext]);
  const history = useHistory();
  const {
    channel, isDebugMode, clientStore, cookie, deliveryZip: serverDeliveryZip
  } = useContext(ExperienceContext);
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;
  const { pageType, prefetchPip, productPodHoverDelay } = props;
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const prefetchedSkuSet = useRef(new Set());
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const isNoResultsFoundRef = useRef(false);
  const { storeId, storeZip } = store;

  const searchNavPropsRef = useRef({
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT,
    askForSponsored: false,
    deliveryZip: serverDeliveryZip
  });

  if (clientStore) {
    let deliveryZipCode;
    if (typeof window !== 'undefined') {
      deliveryZipCode = window.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails() || {};
    }
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode.zipcode
    };
  }

  const { isClientResolved } = useContext(QueryContext);

  const renderPlaceholder = (browseScheme = '', searchScheme = '', defer = true) => {
    const schemeName = isBrowse ? browseScheme : searchScheme;

    if (!schemeName) return null;

    const isBanner = [
      'cat_sponsored__banner',
      'browse_sponsored__banner'
    ].indexOf(browseScheme) > -1
      || searchScheme === 'search_sponsored__banner';

    let lgName = `recs:${schemeName}`;
    if (schemeName.indexOf('sponsored') > -1) {
      if (isBanner) {
        lgName = 'sponsored-banner';
      } else {
        lgName = 'sponsored-carousel';
      }
    }

    const gridClass = classNames({ 'grid--card grid-space-top': !isBanner });
    return (
      <LayoutGroup name={`${lgName}`}>
        <Row>
          <Col nopadding>
            <div className={gridClass}>
              <RecommendationsPlaceholder scheme={schemeName} defer={defer} />
            </div>
          </Col>
        </Row>
      </LayoutGroup>
    );
  };

  const onStoreChange = ({ output }) => {
    const { storeId: updatedStoreId } = output || {};
    if (!updatedStoreId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer
        .localize({ storeId: updatedStoreId })
        .then(() => {
          window.location.reload();
        });
    }
  };

  useEffect(() => {
    const el = document.querySelector('.Header3');
    if (!el) return;
    el.classList.add('Header3-sticky-desktop');
  }, []);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
  }, []);

  const onDimensionsChange = useCallback(({ refinement, onDisplay }) => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    const updatedPath = updateQueryParams(refinement.url, [
      {
        paramName: 'onDisplay',
        paramValue: onDisplay,
      }
    ]);
    history.push(updatedPath);
  }, [history]);

  // TODO: This fcn needs to be combined with onMultiStoreChange to preserve both params. Both features are co-dependent
  const onDisplayChange = useCallback(
    ({ useStoreFilter }) => {
      const updatedPath = updateQueryParams(path, [
        {
          paramName: 'onDisplay',
          paramValue: useStoreFilter,
        }
      ]);
      history.push(updatedPath);
    },
    [history, path]
  );

  const resultsDimensionsRef = useRef(null);
  const onPageChange = useCallback(({ url }) => {
    history.push(url);

    const { offsetTop } = resultsDimensionsRef?.current?.parentElement?.parentElement;
    window.scrollTo(0, offsetTop || 0);
  }, [history]);

  const onSortChange = useCallback(({
    sortby, sortorder, name, currentPage, page, updatedPath = null
  }) => {
    const newPath = updateQueryParams(updatedPath || path, [
      {
        paramName: 'Nao',
        paramValue: null
      },
      {
        paramName: 'sortorder',
        paramValue: sortorder
      },
      {
        paramName: 'sortby',
        paramValue: sortby
      }
    ]);
    window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
      name,
      currentPage,
      page
    });
    history.push(newPath);
  }, [history, path]);

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const hideOnNoCategory = /Ntt-/.test(path);

  const isEtchVisNavOnPLP = (data) => {
    try {
      const stringifiedPartialTemplates = data?.searchModel?.partialTemplates;
      const isPLPPage = data?.searchModel?.metadata?.contentType === 'plppage';
      const etchVisualNavExists = (stringifiedPartialTemplates?.[0] || '').indexOf('visualNavigation') > -1;
      let partialTemplates = [];
      partialTemplates = (stringifiedPartialTemplates || []).map((template) => JSON.parse(template)) || [];
      const plpBanner = partialTemplates?.[0]?.[Object.keys(partialTemplates?.[0])
        ?.find((type) => type === 'PLP_Banner')];
      return (!!plpBanner && plpBanner?.Content?.length > 0) && etchVisualNavExists && isPLPPage;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error parsing etch templates');
      return false;
    }
  };

  const {
    keyword,
    lowerbound,
    Nao = 0,
    sortby,
    nValue = '',
    sortorder,
    upperbound,
    storeSelection = '',
    catStyle,
    skipSpellCheck,
    onDisplay
  } = parseUrl({ path: cleanPath });

  const loadedStoreId = useRef(storeId);
  const initialLoadedStoreId = useRef(storeId);
  const timeoutRef = useRef(null);
  const productPageChunkLoaded = useRef(false);

  const additionalSearchParams = {};
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;
  if (searchNavPropsRef.current.askForSponsored && hasSponsoredSorts && cookie?.adobeCookie?.MCMID) {
    additionalSearchParams.sponsored = true;
    additionalSearchParams.mcvisId = cookie.adobeCookie.MCMID;
  }

  const showProducts = /showproducts/i.test(catStyle);
  if (showProducts) {
    additionalSearchParams.plp = true;
  }

  if (skipSpellCheck) {
    additionalSearchParams.skipSpellCheck = skipSpellCheck;
  }

  if (searchNavPropsRef.current.deliveryZip) {
    additionalSearchParams.deliveryZip = searchNavPropsRef.current.deliveryZip;
  }

  const opts = {
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    pageSize: searchNavPropsRef.current.pageSize,
    storeId,
    storefilter: onDisplay === 'true' ? 'IN_STORE' : 'ALL',
    start: Nao,
    sortby,
    sortorder,
    upperbound
  };

  const response = useProductResults(opts);

  const { data, error, loading, variables } = response;

  const cleanedPath = (pathname || '').replace(/render-\w+\//, '');

  if (data && !loading) {
    loadedStoreId.current = variables.storeId;
  }

  const handleProductClick = useCallback((evt, product) => {
    let canonicalUrl = product?.identifiers?.canonicalUrl;

    if (canonicalUrl) {
      evt.preventDefault();
      canonicalUrl = ProductPodUtils.addSponsoredAnalyticsParams({
        productUrl: canonicalUrl, info: product.info
      });
      history.push(canonicalUrl);
    }
  }, [history]);

  const isCategory = (data?.searchModel?.metadata?.contentType
    && data.searchModel.metadata.contentType === 'categorypage');

  const { products = [], metadata } = data?.searchModel || {};

  const stringifyItemIds = (hookData) => {
    return hookData?.searchModel?.products?.map(({ itemId: sku }) => sku)
      .join('-');
  };

  const hasInStoreFilter = (resultsData) => {
    const { searchModel } = resultsData || {};
    const { appliedDimensions } = searchModel || {};
    const getItFast = (appliedDimensions || [])
      .filter((dimension) => (dimension.label || '').toUpperCase() === 'GET IT FAST');
    if (!getItFast.length) {
      return false;
    }
    const storePickUp = getItFast
      .find((dimension) => dimension.refinements
        .find((refinement) => refinement.label === 'Pick Up Today'));
    return !!storePickUp;
  };

  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const onSelectedForCompare = (id, selected = false) => {
    if (selected) {
      setSelectedForCompare([...selectedForCompare, id]);
    } else {
      setSelectedForCompare(selectedForCompare.filter((item) => item !== id));
    }
  };

  const onCompare = () => {
    let queryparams = selectedForCompare
      .map((item, idx) => `item${idx + 1}=${item}`)
      .join('&');

    history.push({
      pathname: '/compare',
      search: `?${queryparams}`
    }, {
      bypassExperienceCheck: true
    });
  };

  useEffect(() => {
    const itemIds = window.sessionStorage.getItem('compare');
    window.sessionStorage.removeItem('compare');
    if (itemIds) {
      setSelectedForCompare(JSON.parse(itemIds));
    }
  }, []);

  useEffect(() => {
    if (data && !loading) {
      const { stores: storeInfo, productCount = {} } = data?.searchModel?.metadata || {};

      const receivedAllProducts = (clientStore?.storeId === variables?.storeId) && !!storeInfo;
      const hasNoProducts = productCount?.allProducts === '0';
      const shouldTriggerBrowseAndCategoryAnalytics = isCategory
        || (isBrowse && (receivedAllProducts || hasNoProducts));

      if (shouldTriggerBrowseAndCategoryAnalytics) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', data?.searchModel);
      } else if (isSearch && (variables?.pageSize !== 12)) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', data?.searchModel);
      }
    }
  }, [stringifyItemIds(data), metadata?.stores?.storeId]);

  const resultsColProps = {
    xs: '8',
    sm: '9',
    md: '10',
    lg: '10'
  };

  const prefetchPipRef = useRef({
    prefetchPip: alias('product').params({ itemId: stringType().isRequired() }).shape({
      itemId: stringType()
    }),
    prefetchPipClient: alias('product').params({ itemId: stringType().isRequired() }).shape({
      itemId: stringType()
    })
  });

  const dataModelCombo = extend({}, prefetchPipRef.current.prefetchPip, prefetchPipRef.current.prefetchPipClient);

  const [productRequestClient] = useLazyDataModel('prefetchPipClient', {
    fetchPolicy: 'cache-first',
    dataModel: { prefetchPipClient: dataModelCombo },
    skip: true,
    variables: { storeId, dataSource: 'catalog' }
  });

  const fetchProduct = (itemId) => {
    if (prefetchPip === 'both' || prefetchPip === 'clientOnly') {
      productRequestClient({
        variables: {
          itemId,
          dataSource: 'catalog',
          storeId,
          zipCode: storeZip
        }
      });
    }
    // is this supposed to be added even when prefetchPip is off?
    prefetchedSkuSet.current.add(itemId);
  };

  const loadProductPageChunk = async () => {
    if (!productPageChunkLoaded.current) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const productPage = await productPageImport();
      prefetchPipRef.current = productPage?.ProductDataModel;
      productPageChunkLoaded.current = true;
    }
  };

  useEffect(() => {
    if (prefetchPip) {
      timeoutRef.current = setTimeout(() => {
        loadProductPageChunk()
      }, 5000);
    }
  }, []);

  const onHoverProductPod = async (itemId) => {

    // when the first product pod is hovered, we load the product page chunk (async)
    if (prefetchPip) {
      await loadProductPageChunk();
    }

    if (productPageChunkLoaded.current && !prefetchedSkuSet.current.has(itemId)) {
      fetchProduct(itemId);
    }
  };

  const getResxFromData = () => {

    const shouldFetchBanner = (() => {
      const hasBrandCrumb = data?.searchModel?.taxonomy?.breadCrumbs?.some((crumb) => crumb.dimensionName === 'Brand');
      const isFeaturedProductNode = pathname.indexOf('/b/Featured-Products') !== -1;

      const hasBrandSearch = data?.searchModel?.metadata?.analytics?.semanticTokens
        && data.searchModel.metadata.analytics.semanticTokens.indexOf('br:{') !== -1;

      return !(hasBrandCrumb || hasBrandSearch || isFeaturedProductNode);
    })();
    const categoryID = data?.searchModel?.metadata?.categoryID;

    const itemIds = data?.searchModel?.products?.map((product) => {
      return product.identifiers?.itemId;
    });

    const levels = data?.searchModel?.taxonomy?.breadCrumbs?.reduce((acc, cur) => {
      if (!cur.dimensionName) return acc;
      return `${acc}, ${cur.label}`;
    }, '');

    return {
      appId: 'homedepot01',
      carouselOptions: {
        fullScrollSkip: 2
      },
      criteo: {
        version: 'model2'
      },
      dataContext: {
        searchModel: data?.searchModel
      },
      enableEventSchema: true,
      fetchBanner: shouldFetchBanner,
      itemid: categoryID,
      keyword,
      levels,
      maxProducts: 16,
      organicskus: itemIds ? itemIds.toString() : '',
      products: itemIds ? itemIds.toString() : '',
      recsAndSearchDynamicAPIEnabled: true,
      storeId,
    };
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip;
    }
    const isResolved = isClientResolved({ queryName: 'searchModel' });
    if (attributes.product.fulfillment) {
      if (isResolved) {
        return skip;
      }
      return true;
    }
    return skip;
  };

  const isInStoreFilterON = hasInStoreFilter(data);
  const pageNumber = (Nao / searchNavPropsRef.current.pageSize) + 1;
  const isEtchVisNavInPLP = isEtchVisNavOnPLP(data);
  const isDynamicBrandPage = data?.searchModel?.aisles?.length > 0;
  isNoResultsFoundRef.current = isSearch
    && data?.searchModel?.searchReport?.totalProducts === 0
    && !!keyword;

  const productsWithPlaceholders = useMemo(() => {
    let groupOfPods = 0;
    let reduced = products?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < MAX_PODS_TO_HYDRATE) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === MAX_PODS_TO_HYDRATE) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);
    if (products?.length === SERVER_SIDE_PRODUCT_POD_COUNT && (!isClientResolved({ queryName: 'searchModel' }))) {
      const placeholderHydrators = (CLIENT_SIDE_PRODUCT_POD_COUNT - SERVER_SIDE_PRODUCT_POD_COUNT)
      / MAX_PODS_TO_HYDRATE;
      for (let i = 0; i < placeholderHydrators; i += 1) {
        reduced.push(Array(MAX_PODS_TO_HYDRATE).fill());
      }
    }

    return reduced;
  }, [isClientResolved, products]);

  const { current: originalProducts } = useRef(productsWithPlaceholders);
  const [isHydrated, setIsHydrated] = useState(false);
  const hydrators = isHydrated || !originalProducts?.length
    ? productsWithPlaceholders
    : originalProducts;

  if (isNoResultsFoundRef.current) {
    return (
      <NoResultsFound
        keyword={keyword}
        recommendationsCtx={getResxFromData()}
        searchData={data}
        searchError={error}
        searchLoading={loading}
        storeId={initialLoadedStoreId.current}
      />
    );
  }

  return (
    <RecommendationsProvider resx={getResxFromData()}>
      <BrowseSearchRedirector variables={response?.variables} />
      <Row className="isBound browse-search">
        <LayoutManager name="browse-and-search-master" input={{ isDebugMode }} useContextForProps>
          <LayoutGroup name="browse-and-search-metadata">
            <Metadata>
              <BrowseSearchMetadata data={data} loading={loading} error={error} pathWithQueryParams={path} />
            </Metadata>
          </LayoutGroup>
          <LayoutGroup tag={Row} name="breadcrumbs" className="u--paddingTop u--paddingBottom">
            <Col flatten>
              <Breadcrumbs data={data} loading={loading} error={error} hideOnNoCategory={hideOnNoCategory} />
            </Col>
          </LayoutGroup>
          <LayoutGroup tag={Row} name="product-results-group">
            <Col flatten>
              <ProductResults data={data} loading={loading} error={error}>
                <LayoutManager
                  name="product-results"
                  input={{ isDebugMode, isDynamicBrandPage, channel }}
                  useContextForProps
                >
                  {renderPlaceholder(
                    isCategory ? 'cat_sponsored__banner' : 'browse_sponsored__banner',
                    'search_sponsored__banner',
                    false
                  )}

                  <LayoutGroup name="sponsored-banner-nucleus">
                    <Row>
                      <Col>
                        <SponsoredBanner
                          pageContext={{
                            label: 'browse-search',
                            data,
                            keyword,
                            isCategory,
                            isBrowse,
                            isSearch
                          }}
                        />
                      </Col>
                    </Row>
                  </LayoutGroup>

                  <LayoutGroup tag={Row} name="results-header">
                    <Col flatten>
                      <ResultsHeader
                        showShopAllLink={isDynamicBrandPage}
                        resultsRef={resultsDimensionsRef}
                      />
                    </Col>
                  </LayoutGroup>
                  <LayoutGroup tag={Row} name="etch-widgets">
                    <EtchWidgets
                      data={data}
                      loading={loading}
                      error={error}
                      keyword={keyword}
                      navParam={nValue}
                      storeId={loadedStoreId.current}
                      exclude={['plpSeo']}
                    />
                  </LayoutGroup>
                  {!isEtchVisNavInPLP && (
                    <LayoutGroup tag={Row} name="visual-nav">
                      <Col className="visual-nav-container">
                        <VisualCategoryNavigation
                          data={data}
                          loading={loading}
                          disableSwipeNavigation
                          slider
                          showArrows={false}
                        />
                      </Col>
                    </LayoutGroup>
                  )}
                  {!isCategory && (
                    <LayoutGroup tag={Row} name="results-applied">
                      <Col sm="9">
                        <ResultsApplied
                          onAppliedChange={onDimensionsChange}
                          onDisplay={onDisplay}
                          onDisplayChange={onDisplayChange}
                        />
                      </Col>
                      <Col xs="3">
                        <ResultsSortBy onSortChange={onSortChange} />
                      </Col>
                    </LayoutGroup>
                  )}

                  {!isCategory && (
                    <LayoutGroup
                      tag={Row}
                      name="results-dimensions"
                      className="browse-search__left-outer-padding"
                    >
                      <Col xs="4" sm="3" md="2" lg="2">
                        <Hydrator
                          id="browse-search-dimensions"
                          scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                        >
                          <ResultsDimensions
                            onDimensionsChange={onDimensionsChange}
                            onDisplay={onDisplay}
                            onDisplayChange={onDisplayChange}
                            shiftCategoryDimension={isDynamicBrandPage}
                          />
                        </Hydrator>

                        <LayoutManager name="sponsored-skyscraper-banner-wrapper" useContextForProps>
                          <LayoutGroup name="sponsored-skyscraper-banner">
                            <Hydrator
                              id="browse-search-dimensions"
                              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                            >
                              <Row>
                                <Col>
                                  <SponsoredSkyscraperBanner
                                    pageContext={{
                                      label: 'browse-search',
                                      data,
                                      keyword,
                                      isCategory,
                                      isBrowse,
                                      isSearch
                                    }}
                                  />
                                </Col>
                              </Row>
                            </Hydrator>
                          </LayoutGroup>
                        </LayoutManager>

                      </Col>
                      <Col
                        className="browse-search__pod-col-no-padding"
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...resultsColProps}
                      >
                        <LayoutManager
                          name="product-results-inner"
                          useContextForProps
                          input={{ isDebugMode, isDynamicBrandPage, channel }}
                        >
                          {isDynamicBrandPage && (
                            <LayoutGroup name="dynamic-aisles">
                              <DynamicBroadIntentAisle
                                searchResponse={response}
                                storeId={loadedStoreId.current}
                              />
                            </LayoutGroup>
                          )}
                          <LayoutGroup tag={Row} name="results-applied">
                            <Col flatten>
                              <Col sm="9">
                                <ResultsApplied onAppliedChange={onDimensionsChange} />
                              </Col>
                              <Col xs="3">
                                <ResultsSortBy onSortChange={onSortChange} />
                              </Col>
                            </Col>
                          </LayoutGroup>
                          <LayoutGroup name="wrapped-results">
                            <ResultsWrapped>
                              <div className="grid" ref={resultsDimensionsRef}>
                                {!products?.length && loading && isSearch && (
                                  <ProductPodPlaceholder podsCount={12} />
                                )}
                                {!!hydrators?.length && (
                                  <QueryProvider
                                    dataSource="searchNav"
                                    defaultVariables={{ storeId }}
                                    skip={skipFn}
                                    cacheKey="search-product-pods"
                                  >
                                    {hydrators?.map((group, hydratorIndex) => {
                                      if (!group?.length) return null;
                                      return (
                                        <Hydrator
                                          afterHydrate={() => { setIsHydrated(true) }}
                                          id={`browse-search-pods${hydratorIndex + 1}`}
                                          className="grid"
                                          scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                                          patch={!hydratorIndex}
                                          key={`hydrator-${hydratorIndex}`}
                                        >
                                          <HoistedProductPodGroup
                                            DetailsPod={CustomProductPodDetails}
                                            keyword={keyword}
                                          >
                                            {group?.map((product, itemIndex, productArray) => {
                                              const { itemId } = product?.identifiers || {};
                                              if (!itemId) {
                                                return (
                                                  <Col
                                                    className="placeholder-product-pod"
                                                    key={`placeholder-${itemIndex}`}
                                                    lg="3"
                                                    md="4"
                                                    sm="4"
                                                    xs="6"
                                                  >
                                                    <RectShape
                                                      style={{ width: '100%', height: '250px', marginBottom: '10px' }}
                                                      color="#E0E0E0"
                                                    />
                                                    <TextBlock rows={7} color="#E0E0E0" />
                                                  </Col>
                                                );
                                              }
                                              const categoryContentCard = (hydratorIndex * MAX_PODS_TO_HYDRATE)
                                                  + itemIndex === 12;
                                              const podCount = productArray?.length;
                                              const fallback = '6';
                                              return (
                                                <LayoutGroup
                                                  index={itemIndex}
                                                  key={`${itemId}-${itemIndex}`}
                                                  id={`plp-product-pod__${itemIndex}`}
                                                  name={`Product Pod: ${itemIndex}`}
                                                >
                                                  {categoryContentCard
                                                    && (
                                                      <CategoryCards
                                                        data={data}
                                                        loading={loading}
                                                        error={error}
                                                        storeId={loadedStoreId.current}
                                                      />
                                                    )}
                                                  <Col
                                                    lg="3"
                                                    md="4"
                                                    sm="4"
                                                    xs="6"
                                                    fallback={fallback}
                                                    className="browse-search__pod"
                                                  >
                                                    <ProductPodDefault
                                                      itemId={itemId}
                                                      storeId={loadedStoreId.current}
                                                      onClick={handleProductClick}
                                                      position={itemIndex}
                                                      mobilePodType="singleColumn"
                                                      installationServices
                                                      showModelNumber
                                                      showStoreInfo
                                                      podCount={podCount}
                                                      pageNumber={pageNumber}
                                                      hasInStoreFilter={isInStoreFilterON}
                                                      hoverDelay={productPodHoverDelay}
                                                      onHover={onHoverProductPod}
                                                      pageType={pageType}
                                                      // preFetchProductData
                                                      comparable
                                                      onCompare={onCompare}
                                                      selectedForCompare={selectedForCompare}
                                                      onSelectedForCompare={onSelectedForCompare}
                                                      showKPF
                                                    />
                                                  </Col>
                                                </LayoutGroup>
                                              );
                                            })}
                                          </HoistedProductPodGroup>
                                        </Hydrator>
                                      )
                                    })}
                                  </QueryProvider>
                                )}
                              </div>
                            </ResultsWrapped>
                          </LayoutGroup>
                          <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="compare">
                            <Col>
                              <CompareTray
                                itemIds={selectedForCompare}
                                onRemove={onSelectedForCompare}
                                onCompare={onCompare}
                              />
                            </Col>
                          </LayoutGroup>
                          {isSearch && (
                            <DynamicComponent pageType={pageType}>
                              <Hydrator
                                id="search-seo-section"
                                className="grid"
                                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                              >
                                <LayoutGroup
                                  tag={Row}
                                  name="related-search"
                                  className="u--paddingTop u--paddingBottom"
                                >
                                  <Col nopadding>
                                    <RelatedSearch
                                      navParam={nValue}
                                      searchData={data}
                                      searchError={error}
                                      searchLoading={loading}
                                      storeId={initialLoadedStoreId.current}
                                    />
                                  </Col>
                                </LayoutGroup>
                              </Hydrator>
                            </DynamicComponent>
                          )}
                          <DynamicComponent pageType={pageType}>
                            <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="pagination">
                              <Col>
                                <ResultsPagination
                                  pageSize={searchNavPropsRef.current.pageSize}
                                  onPageChange={onPageChange}
                                  path={path}
                                />
                              </Col>
                            </LayoutGroup>
                          </DynamicComponent>
                          <DynamicComponent pageType={pageType}>
                            <Hydrator
                              id="feedback"
                              className="grid"
                              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                            >
                              <LayoutGroup
                                tag={Row}
                                className="u--paddingTop u--paddingBottom"
                                name="search-feedback"
                              >
                                <Col>
                                  <SearchFeedback />
                                </Col>
                              </LayoutGroup>
                            </Hydrator>
                          </DynamicComponent>
                        </LayoutManager>
                      </Col>
                    </LayoutGroup>
                  )}

                </LayoutManager>
              </ProductResults>
            </Col>
          </LayoutGroup>
          {renderPlaceholder(
            isCategory ? 'cat_sponsored' : 'browse_sponsored',
            'search_sponsored',
            false
          )}
          {renderPlaceholder(
            'PLP_Browse',
            'searchViewed'
          )}
          {renderPlaceholder(
            '',
            'plpsearch_multi'
          )}
          {renderPlaceholder(
            isCategory ? 'rv_categorypages_rr' : 'rv_nav_plp_rr',
            'rv_search_plp_rr'
          )}
          {!isSearch && (
            <DynamicComponent pageType={pageType}>
              <Hydrator
                id="browse-seo-section"
                className="grid"
                waitFor={[SeoGeneratedContentLoad, SeoLinksLoad]}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              >
                <LayoutGroup tag={Row} name="etch-seo">
                  <Col>
                    <EtchWidgets data={data} loading={loading} error={error} include={['plpSeo']} />
                  </Col>
                </LayoutGroup>
                <LayoutGroup tag={Row} name="related-search" className="u--paddingTop u--paddingBottom">
                  <Col nopadding>
                    <RelatedSearch
                      navParam={nValue}
                      searchData={data}
                      searchError={error}
                      searchLoading={loading}
                      storeId={initialLoadedStoreId.current}
                    />
                  </Col>
                </LayoutGroup>
                <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="seo-qanda">
                  <Col>
                    <SeoGeneratedContent nvalue={nValue} />
                  </Col>
                </LayoutGroup>
                <LayoutGroup tag={Row} name="seo-mmg" className="u--paddingTop u--paddingBottom">
                  <Col nopadding>
                    <SeoLinks path={cleanedPath} />
                  </Col>
                </LayoutGroup>
              </Hydrator>
            </DynamicComponent>
          )}
        </LayoutManager>
        <BackToTop />
      </Row>
    </RecommendationsProvider>
  );
};

BrowseAndSearch.propTypes = {
  pageType: string.isRequired,
  prefetchPip: string,
  productPodHoverDelay: number
};

BrowseAndSearch.defaultProps = {
  prefetchPip: null,
  productPodHoverDelay: 500
};

BrowseAndSearch.dataModel = extend(
  {
    searchModel: params()
      .shape({
        metadata: shapeType({
          categoryID: stringType(),
          analytics: shapeType({
            semanticTokens: stringType(),
            dynamicLCA: stringType()
          })
        }),
        products: params().arrayOf(shapeType({
          identifiers: shapeType({
            storeSkuNumber: client(stringType())
          })
        }))
      }),
  },
  ResultsDimensions,
  ResultsFilterAndSort,
  BrowseSearchRedirector,
  BrowseSearchMetadata,
  Breadcrumbs,
  EtchWidgets,
  VisualCategoryNavigation,
  AisleBay,
  CategoryCards,
  CompareTray,
  SeoGeneratedContentModel,
  SeoLinksModel,
  RelatedSearch,
  RecommendationsPlaceholder,
  productDefaultResultsDataModel,
  DynamicBroadIntentAisle
);
